/**
 * iOS Keyboard Viewport Fix
 * 
 * Fixes the iOS PWA issue where the keyboard closing causes the viewport
 * to permanently shift up into the status bar when app is added to homescreen.
 * 
 * This utility carefully manages viewport restoration without aggressive scrollTo calls
 * which can themselves trigger iOS viewport bugs.
 * 
 * Compatible with pull-to-refresh functionality - only intervenes when necessary.
 */

let isInitialized = false;
let lastFocusTime = 0;

/**
 * Initialize iOS keyboard viewport fix
 * Call this once in your app initialization
 */
export const initViewportHeight = () => {
  if (isInitialized) return;
  
  // Check if running on iOS (including iPad)
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  
  // Only apply fix on iOS devices
  if (!isIOS) {
    isInitialized = true;
    return;
  }
  
  // Track previous height to detect keyboard closing
  let previousHeight = window.innerHeight;
  let previousWidth = window.innerWidth;
  
  // Track focus events to distinguish keyboard-related resizes from other resizes
  document.addEventListener('focusin', () => {
    lastFocusTime = Date.now();
  }, { passive: true });

  // Listen for resize events to detect keyboard closing
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      const currentHeight = window.innerHeight;
      const currentWidth = window.innerWidth;
      const timeSinceLastFocus = Date.now() - lastFocusTime;
      
      // Only trigger viewport reset if:
      // 1. Height increased (keyboard potentially closing)
      // 2. Width stayed the same (not orientation change)
      // 3. Recent focus event occurred (within last 5 seconds)
      // 4. We're actually scrolled (avoid unnecessary scrollTo)
      // This prevents interference with pull-to-refresh
      if (currentWidth === previousWidth && 
          currentHeight > previousHeight && 
          timeSinceLastFocus < 5000 &&
          window.scrollY > 0) {
        // Use scrollTo with options for smoother behavior
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }
      
      previousHeight = currentHeight;
      previousWidth = currentWidth;
    }, 100);
  }, { passive: true });
  
  // Listen for visualViewport resize (more reliable on some iOS versions)
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const currentHeight = window.innerHeight;
        const currentWidth = window.innerWidth;
        const timeSinceLastFocus = Date.now() - lastFocusTime;
        
        // Same conditions as above to prevent pull-to-refresh interference
        if (currentWidth === previousWidth && 
            currentHeight > previousHeight && 
            timeSinceLastFocus < 5000 &&
            window.scrollY > 0) {
          window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        }
        
        previousHeight = currentHeight;
        previousWidth = currentWidth;
      }, 100);
    }, { passive: true });
  }
  
  // Additional safeguard: Listen for focusout events (when inputs lose focus)
  // This catches cases where the resize event doesn't fire reliably
  document.addEventListener('focusout', (e) => {
    // Only for input/textarea elements
    if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) {
      // Small delay to allow keyboard to fully close
      setTimeout(() => {
        // Only scroll if we're actually scrolled
        if (window.scrollY > 0) {
          window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        }
      }, 100);
    }
  }, { passive: true });
  
  isInitialized = true;
};
